import { useState, useEffect } from 'react';
import { Toaster, toast } from 'sonner';
import { Navigation } from './components/Navigation';
import { Footer } from './components/Footer';
import { HomePage } from './components/HomePage';
import { WhoWeArePage } from './components/WhoWeArePage';
import { ExpertisePage } from './components/ExpertisePage';
import { ProjectsPage } from './components/ProjectsPage';
import { ProjectDetailsPage } from './components/ProjectDetailsPage';
import { ContactPage } from './components/ContactPage';
import { NewsPage } from './components/NewsPage';
import { PrivacyPage } from './components/PrivacyPage';
import { TeamPage } from './components/TeamPage';
import { CareersPage } from './components/CareersPage';
import { JobDetailsPage } from './components/JobDetailsPage';
import { AdminLoginPage } from './components/admin/AdminLoginPage';
import { AdminDashboard } from './components/admin/AdminDashboard';
import React from 'react';
import { getJobs, getJobDetails } from './services/Job';
import { hasAdminSession } from './services/authSession';

interface Job {
  id: number;
  title: string;
  department: string;
  location: string;
  type: string;
  description: string;
  qualifications: string[] | string | null;
  education: string;
  responsibilities: string[];
  createdDate: string;
}

// ------------------ TOKEN VALIDATION ------------------
const isTokenValid = hasAdminSession;

// ------------------ JOB ID EXTRACTION ------------------
const extractJobIdFromUrl = (): number | null => {
  const path = window.location.pathname;

  if (path.startsWith('/job/')) {
    const jobPath = path.split('/job/')[1];
    if (jobPath) {
      const match = jobPath.match(/-(\d+)$/);
      if (match) {
        return parseInt(match[1], 10);
      }

      const id = parseInt(jobPath, 10);
      if (!isNaN(id)) {
        return id;
      }
    }
  }
  return null;
};

// ------------------ CACHE SETUP ------------------
let jobsCache: Job[] | null = null;
let jobsCacheTime: number = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// ------------------ MAIN APP ------------------
export default function App() {
  const [currentPage, setCurrentPage] = useState('home');
  const [selectedJobId, setSelectedJobId] = useState<number | null>(null);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [applications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [initialArticleSlug, setInitialArticleSlug] = useState<string | undefined>(undefined);
  const [projectDetailsId, setProjectDetailsId] = useState<string | null>(null);

  // ------------------ CHECK ADMIN TOKEN ------------------
  useEffect(() => {
    const token = localStorage.getItem('admin_access_token');
    if (token && isTokenValid(token)) {
      setIsAdminAuthenticated(true);
    } else {
      localStorage.removeItem('admin_access_token');
      localStorage.removeItem('admin_refresh_token');
      setIsAdminAuthenticated(false);
    }
  }, []);

  // ------------------ PREFETCH JOBS ------------------
  useEffect(() => {
    const preloadJobs = async () => {
      const now = Date.now();
      if (jobsCache && now - jobsCacheTime < CACHE_DURATION) {
        setJobs(jobsCache);
        setInitialLoad(false);
        return;
      }

      try {
        const response = await getJobs();
        const jobsData = response.data || response;
        jobsCache = jobsData;
        jobsCacheTime = now;
        setJobs(jobsData);
      } catch (error) {
        console.error('Error pre-loading jobs:', error);
      } finally {
        setInitialLoad(false);
      }
    };

    preloadJobs();
  }, []);

  // ------------------ INITIAL ROUTE DETECTION ------------------
  useEffect(() => {
    const rawPath = window.location.pathname;
    const path = rawPath.replace(/\/$/, ""); // normalize trailing slash

    if (path.startsWith('/admin')) {
      handleAdminRoute(path);
    } else if (path.startsWith('/job/')) {
      handleJobRoute();
    } else if (path.startsWith('/news/')) {
      // /news/article-slug
      const slug = path.split('/news/')[1];
      if (slug) {
        setInitialArticleSlug(slug);
        setCurrentPage('news');
      } else {
        setCurrentPage('news');
      }
    } else if (path.startsWith('/project/')) {
      // /project/title-slug-123  → extract last numeric segment as ID
      const segment = path.split('/project/')[1] ?? '';
      const idMatch = segment.match(/-(\d+)$/);
      const projectId = idMatch ? idMatch[1] : null;
      setProjectDetailsId(projectId);
      setCurrentPage('project-details');
    } else {
      handleRegularRoute(path);
    }
  }, []);

  // ------------------ ADMIN ROUTES ------------------
  const handleAdminRoute = (path: string) => {
    const token = localStorage.getItem('admin_access_token');
    if (token && isTokenValid(token)) {
      setIsAdminAuthenticated(true);
      setCurrentPage('admin');
    } else {
      setIsAdminAuthenticated(false);
      setCurrentPage('admin');
      localStorage.removeItem('admin_access_token');
      localStorage.removeItem('admin_refresh_token');
      if (path !== '/admin') {
        window.history.replaceState({}, '', '/admin');
      }
    }
  };

  // ------------------ JOB ROUTES ------------------
  const handleJobRoute = async () => {
    const jobId = extractJobIdFromUrl();
    if (jobId) {
      const cachedJob = jobsCache?.find(job => job.id === jobId);
      if (cachedJob) {
        setSelectedJob(cachedJob);
        setSelectedJobId(jobId);
        setCurrentPage('job-details');
      } else {
        await handleJobDetailsFromUrl(jobId);
      }
    } else {
      setCurrentPage('careers');
      window.history.replaceState({}, '', '/careers');
    }
  };

  // ------------------ NORMAL ROUTES ------------------
  const handleRegularRoute = (path: string) => {
    const normalizedPath = path.replace(/\/$/, ""); // ensure no trailing slash
    const routeMap: { [key: string]: string } = {
      '/who-we-are': 'who-we-are',
      '/expertise': 'expertise',
      '/projects': 'projects',
      '/contact': 'contact',
      '/news': 'news',
      '/privacy': 'privacy',
      '/team': 'team',
      '/careers': 'careers'
    };

    setCurrentPage(routeMap[normalizedPath] || 'home');
  };

  // ------------------ JOB DETAILS FETCH ------------------
  const handleJobDetailsFromUrl = async (jobId: number) => {
    try {
      setLoading(true);
      const response = await getJobDetails(jobId);
      const jobData = response.data || response;
      setSelectedJob(jobData);
      setSelectedJobId(jobId);
      setCurrentPage('job-details');
    } catch (error) {
      console.error('Error fetching job details from URL:', error);
      setCurrentPage('careers');
      window.history.replaceState({}, '', '/careers');
      toast.error('Job not found or has been removed');
    } finally {
      setLoading(false);
    }
  };

  // ------------------ PAGE NAVIGATION ------------------
  const handlePageChange = (page: string) => {
    setCurrentPage(page);
    setSelectedJobId(null);
    setSelectedJob(null);
    // Reset deep-link state when navigating manually
    if (page !== 'news') setInitialArticleSlug(undefined);
    if (page !== 'project-details') setProjectDetailsId(null);
    window.scrollTo({ top: 0, behavior: 'instant' });

    if (page === 'admin') {
      const token = localStorage.getItem('admin_access_token');
      if (token && isTokenValid(token)) {
        setIsAdminAuthenticated(true);
        window.history.pushState({}, '', '/admin/dashboard');
      } else {
        setIsAdminAuthenticated(false);
        localStorage.removeItem('admin_access_token');
        localStorage.removeItem('admin_refresh_token');
        window.history.pushState({}, '', '/admin');
      }
    } else {
      const pagePaths: { [key: string]: string } = {
        home: '/',
        'who-we-are': '/who-we-are',
        expertise: '/expertise',
        projects: '/projects',
        contact: '/contact',
        news: '/news',
        privacy: '/privacy',
        team: '/team',
        careers: '/careers'
      };
      window.history.pushState({}, '', pagePaths[page] || '/');
    }
  };

  // ------------------ JOB DETAILS NAVIGATION ------------------
  const handleViewJobDetails = async (jobId: number, jobTitle: string) => {
    const cachedJob = jobsCache?.find(job => job.id === jobId);
    const jobSlug = jobTitle
      .replace(/\s+/g, '-')
      .replace(/[^\w\-]/g, '')
      .toLowerCase();

    if (cachedJob) {
      setSelectedJob(cachedJob);
      setSelectedJobId(jobId);
      setCurrentPage('job-details');
      window.history.pushState({}, '', `/job/${jobSlug}-${jobId}`);
      return;
    }

    try {
      setLoading(true);
      const response = await getJobDetails(jobId);
      const jobData = response.data || response;
      setSelectedJob(jobData);
      setSelectedJobId(jobId);
      setCurrentPage('job-details');
      window.history.pushState({}, '', `/job/${jobSlug}-${jobId}`);
    } catch (error) {
      console.error('Error fetching job details:', error);
      toast.error('Error loading job details');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToCareers = () => {
    setSelectedJobId(null);
    setSelectedJob(null);
    setCurrentPage('careers');
    window.history.pushState({}, '', '/careers');
  };

  const handleAdminLogin = () => {
    setIsAdminAuthenticated(true);
    setCurrentPage('admin');
    window.history.pushState({}, '', '/admin/dashboard');
  };

  const handleAdminLogout = () => {
    setIsAdminAuthenticated(false);
    localStorage.removeItem('admin_access_token');
    localStorage.removeItem('admin_refresh_token');
    setCurrentPage('home');
    window.history.pushState({}, '', '/');
  };

  const handleAdminNavigation = (path: string) => {
    const token = localStorage.getItem('admin_access_token');
    if (!token || !isTokenValid(token)) {
      handleAdminLogout();
      return;
    }
    window.history.pushState({}, '', `/admin${path}`);
  };

  // ------------------ FAST LOADING SPINNER ------------------
  const FastLoadingSpinner = () => (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-3 text-gray-600 text-sm">Loading...</p>
      </div>
    </div>
  );

  // ------------------ RENDER LOGIC ------------------
  const renderPage = () => {
    if (initialLoad && currentPage === 'home') {
      return <FastLoadingSpinner />;
    }

    if (currentPage === 'admin') {
      return !isAdminAuthenticated ? (
        <AdminLoginPage onLogin={handleAdminLogin} />
      ) : (
        <AdminDashboard
          onLogout={handleAdminLogout}
          applications={applications}
          onNavigation={handleAdminNavigation}
        />
      );
    }

    if (currentPage === 'job-details') {
      if (loading) return <FastLoadingSpinner />;
      if (selectedJob)
        return <JobDetailsPage job={selectedJob} onBack={handleBackToCareers} />;
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <p className="text-gray-600 mb-4">Job not found</p>
            <button
              onClick={handleBackToCareers}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 cursor-pointer text-sm"
            >
              Back to Careers
            </button>
          </div>
        </div>
      );
    }

    switch (currentPage) {
      case 'home':
        return <HomePage onPageChange={handlePageChange} />;
      case 'who-we-are':
        return <WhoWeArePage />;
      case 'expertise':
        return <ExpertisePage />;
      case 'projects':
        return <ProjectsPage />;
      case 'project-details':
        return <ProjectDetailsPage projectId={projectDetailsId} />;
      case 'contact':
        return <ContactPage />;
      case 'news':
        return (
          <NewsPage
            initialArticleSlug={initialArticleSlug}
            onArticleNavigate={(slug) => setInitialArticleSlug(slug ?? undefined)}
          />
        );
      case 'privacy':
        return <PrivacyPage />;
      case 'team':
        return <TeamPage />;
      case 'careers':
        return (
          <CareersPage
            onViewJobDetails={handleViewJobDetails}
            jobs={jobs}
            loading={initialLoad}
          />
        );
      default:
        return <HomePage onPageChange={handlePageChange} />;
    }
  };

  // ------------------ FINAL RENDER ------------------
  if (currentPage === 'admin') {
    return (
      <div className="min-h-screen">
        {renderPage()}
        <Toaster />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation
        currentPage={currentPage === 'project-details' ? 'projects' : currentPage}
        onPageChange={handlePageChange}
      />
      <main className="flex-1">
        {initialLoad && currentPage !== 'job-details' ? (
          <FastLoadingSpinner />
        ) : (
          renderPage()
        )}
      </main>
      <Footer onPageChange={handlePageChange} />
      <Toaster />
    </div>
  );
}
